import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { Design, Document } from 'blaze-ui/icons'
import { AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MailchimpBrand } from 'blaze-ui/icons/35'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import Styles from './TargetIcons.module.scss'

interface TargetIconsProps {
  item: AlmanacFileIndexItem
}

export const TargetIcons = ({ item }: TargetIconsProps) => {
  const { asset_type, channel, scheduled_post_targets } = item
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()

  const scheduledPostTargets = scheduled_post_targets || []
  const isScheduledPostsV3 = useIsScheduledPostsV3()
  const targetsWithProviders = scheduledPostTargets.filter((target) => !!target.provider)

  // If ScheduledPostsV3 is enabled and there are targets with providers, render them
  if (isScheduledPostsV3 && targetsWithProviders.length > 0) {
    return (
      <div className={Styles.targetIconsContainer}>
        {targetsWithProviders.map((target) => (
          <div key={target.id} className={Styles.targetIcon}>
            <PlatformIcon platform={target.provider} size={18} isBrand />
          </div>
        ))}
      </div>
    )
  }

  // Fallback logic for when there are no targets
  // For articles: check channel type and render blogPlatformBranding or mailchimp icon
  if (asset_type === 'Article') {
    if (channel === ChannelType.Newsletter) {
      return (
        <div className={Styles.fallbackIcon}>
          <MailchimpBrand size={18} />
        </div>
      )
    }

    if (channel === ChannelType.BlogPost) {
      const blogPlatformBranding = getBlogPlatformBrandingWithOnboardingSelection({
        channel,
        externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
        selectedBlogPlatform: brandPlan?.selected_blog_platform,
        featureFlags: {
          goHighLevelEnabled,
          wixEnabled,
          blogPlatformOnboardingBrandingEnabled,
          shopifyEnabled,
        },
      })

      return (
        <div className={Styles.fallbackIcon}>
          <ChannelIcon
            size={18}
            channel={channel}
            isTransparentBackground={false}
            blogPlatform={blogPlatformBranding}
          />
        </div>
      )
    }

    if (channel) {
      return (
        <div className={Styles.fallbackIcon}>
          <ChannelIcon size={18} channel={channel} isTransparentBackground={false} />
        </div>
      )
    }

    // Article with no channel
    return (
      <div className={Styles.fallbackIcon}>
        <Document color="var(--dark-80)" size={18} />
      </div>
    )
  }

  // For designs with no targets
  if (asset_type === 'Design') {
    // If there's a channel but 0 targets, render the channel icon
    if (channel && scheduledPostTargets.length === 0) {
      return (
        <div className={Styles.fallbackIcon}>
          <ChannelIcon size={18} channel={channel} isTransparentBackground={false} />
        </div>
      )
    }

    return (
      <div className={Styles.fallbackIcon}>
        <Design color="var(--dark-80)" size={18} />
      </div>
    )
  }

  // General fallback: if there's a channel with 0 targets, render the channel icon
  if (channel && scheduledPostTargets.length === 0) {
    return (
      <div className={Styles.fallbackIcon}>
        <ChannelIcon size={18} channel={channel} isTransparentBackground={false} />
      </div>
    )
  }

  return null
}
